import {
	Button,
	Checkbox,
	Dropdown,
	Menu,
	Pagination,
	Space,
	Spin,
	Tooltip,
	message,
} from 'antd';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useTypedDispatch, useTypedSelector } from '@stores/index';
import { getCustomRomSessionById, setSelectedRom } from '@stores/rom/customRom';
import {
	ICustomRomSession,
	ICustonRomSessionPaginated,
	IOnchangeStatus,
	IstatusIcon,
	Status,
} from '@stores/interfaces';
import { addReportId, deleteReportId } from '@stores/report/reports';
import { useNavigate } from 'react-router-dom';
import Badge from '@atoms/Badge';
import { StatusNormalized, URL_PATH } from '@stores/constants';
import { updateOmniRomSessionStatus as updateOmniRomSessionStatusOutOfParams } from '@stores/outOfParams';
import { updateOmniRomSessionStatus as updateOmniRomSessionStatusPendingReview } from '@stores/pendingReview';
import { updateOmniRomSessionStatus as updateOmniRomSessionStatusReviewed } from '@stores/reviewed';
import { updateOmniRomSessionStatus as updateOmniRomSessionStatusEscalationRequired } from '@stores/escalationRequired';
import { updateOmniRomSessionStatus as updateOmniRomSessionStatusFollowUpRequired } from '@stores/followUpRequired';
import { SummaryResultReportDownloader } from './SummaryResultReportDownloader';
import { getRomEmailTemplate } from '@stores/settings/setings';
import { FilterLines } from '@carespace-icons/general/filterLines';

interface ICustomSummarySession {
	session: string;
	programId?: string;
}

const CustomSummarySession = (props: ICustomSummarySession) => {
	const { session } = props;
	const { t } = useTranslation();
	const [selectedButton, setSelectedButton] = useState<string>('completed');
	const [completedStatus, setCompletedStatus] = useState<boolean>(true);

	const options = [
		{
			label: t('Admin.data.menu.userRoles.filter'),
			value: 'filter',
			icon: <FilterLines color="stroke-black" width={20} height={18} />,
		},
		{ label: t('Admin.data.survey.completed'), value: 'completed' },
		{ label: t('Admin.data.menu.userRoles.inCompleted'), value: 'incompleted' },
	];
	const dispatch = useTypedDispatch();
	const [customRomSessionExercise, setCustomRomSessionExercise] =
		useState<ICustonRomSessionPaginated>();
	const [isLoading, setLoading] = useState(false);

	const fetchSessionData = async (
		sessionId: string,
		page: number,
		completed: boolean,
	) => {
		setLoading(true);
		const data = await dispatch(
			getCustomRomSessionById({ customRomId: sessionId, page, completed }),
		);
		setCustomRomSessionExercise(data.payload as ICustonRomSessionPaginated);
		setLoading(false);
	};

	const navigate = useNavigate();
	const user = useTypedSelector(state => state.user);
	const { selectedUser } = useTypedSelector(state => state.contacts.main);

	const statusIcons: IstatusIcon = {
		outOfParams: <Badge color="black" />,
		pendingReview: <Badge color="yellow" />,
		reviewed: <Badge color="green" />,
		escalationRequired: <Badge color="rose" />,
		followUpRequired: <Badge color="orange" />,
	};

	type IUpdatePostureSessionStatus = Omit<
		Record<Status, unknown>,
		'newPatients'
	>;

	const updateOmniRomStatus: IUpdatePostureSessionStatus = {
		outOfParams: updateOmniRomSessionStatusOutOfParams,
		pendingReview: updateOmniRomSessionStatusPendingReview,
		reviewed: updateOmniRomSessionStatusReviewed,
		escalationRequired: updateOmniRomSessionStatusEscalationRequired,
		followUpRequired: updateOmniRomSessionStatusFollowUpRequired,
	};

	const onChangeStatus = async (params: IOnchangeStatus, item: unknown, completed: boolean) => {
		const { sessionId, status } = params;
		message.loading({
			content: t('Admin.data.menu.aiAssistant.aiAssistantRomSummary.saving'),
			key: sessionId,
		});
		await dispatch(
			updateOmniRomStatus[item.status as keyof IUpdatePostureSessionStatus]({
				sessionId,
				body: { status },
			}),
		);
		message.success({
			content: t(
				'Admin.data.menu.aiAssistant.aiAssistantRomSummary.statusSuccess',
			),
			key: sessionId,
			duration: 3,
		});
		const data = await dispatch(
			getCustomRomSessionById({
				customRomId: session,
				page: customRomSessionExercise?.pagination?.currentPage || 1,
				completed: completed
			}),
		);
		setCustomRomSessionExercise(data.payload as ICustonRomSessionPaginated);
	};

	const onPageChange = async (pageNumber: number) => {
		setLoading(true);
		fetchSessionData(session, pageNumber, completedStatus);
		setLoading(false);
	};
	useEffect(() => {
		fetchSessionData(session, 1, completedStatus);
	}, [session]);

	useEffect(() => {
		fetchData();
	}, []);

	const fetchData = async () => {
		await dispatch(getRomEmailTemplate());
	};

	const reportIds = useTypedSelector(state => state.reports.reportIds);

	const healthStatus = (session: { id: string; status: Status }) => {
		const menu = (
			<Menu>
				{Object.entries(StatusNormalized)
					.filter(([key]) => key !== session.status)
					.map(([status, label]) => {
						return (
							<Menu.Item
								key={status}
								onClick={() => {
									onChangeStatus(
										{ sessionId: session.id, status: status as Status },
										session,
										completedStatus
									);
								}}>
								<Space>
									<span className="dropdown-badge">
										{statusIcons[status as keyof IstatusIcon]}
									</span>
									<span>{label}</span>
								</Space>
							</Menu.Item>
						);
					})}
			</Menu>
		);
		return (
			<div style={{ paddingRight: 0, display: 'flex', gap: 12 }}>
				{session?.romPatientResults?.length >= 1 && <>
				<Tooltip
					title={t('Admin.data.menu.aiAssistant.aiAssistantRomSummary.edit')}>
					<span>
						<SummaryResultReportDownloader
							selectedRom={session}
							completedStatus={completedStatus}
							fetchSessionData={fetchSessionData}
							user={user.isPhysioterapist ? selectedUser : user}
							reportType="edit"
							color="black"
						/>
					</span>
				</Tooltip>
				{completedStatus &&
				<>
				<Tooltip
					title={t('Admin.data.menu.aiAssistant.aiAssistantRomSummary.qrCode')}>
					<span>
						<SummaryResultReportDownloader
							selectedRom={session}
							completedStatus={completedStatus}
							user={user.isPhysioterapist ? selectedUser : user}
							reportType="qr"
							color="black"
						/>
					</span>
				</Tooltip>
				<Tooltip
					title={t(
						'Admin.data.menu.aiAssistant.aiAssistantRomSummary.sendEmail',
					)}>
					<span>
						<SummaryResultReportDownloader
							selectedRom={session}
							completedStatus={completedStatus}
							user={user.isPhysioterapist ? selectedUser : user}
							reportType="email"
							color="black"
						/>
					</span>
				</Tooltip>
				<Tooltip
					title={t(
						'Admin.data.menu.aiAssistant.aiAssistantRomSummary.download',
					)}>
					<span>
						<SummaryResultReportDownloader
							selectedRom={session}
							user={user.isPhysioterapist ? selectedUser : user}
							reportType="download"
							color="black"
							completedStatus={completedStatus}
						/>
					</span>
				</Tooltip></>}</>}
				<div className="posture-dropdown-section" style={{ paddingRight: 0 }}>
					{user.isPhysioterapist ? (
						<Dropdown overlay={menu}>
							<a onClick={e => e.stopPropagation()}>
								{statusIcons[session.status as keyof IstatusIcon]}
							</a>
						</Dropdown>
					) : (
						''
					)}
				</div>
				<div
					style={{ paddingRight: 0 }}
					onClick={event => {
						event.stopPropagation();
					}}>
					<Checkbox
						checked={reportIds?.romSessionsIds?.includes(session.id)}
						disabled={session?.romPatientResults?.length <= 0}
						className="report-checkbox"
						onChange={e => {
							if (e.target.checked) {
								dispatch(
									addReportId({
										type: 'romSessionsIds',
										id: session.id,
									}),
								);
							} else {
								dispatch(
									deleteReportId({
										type: 'romSessionsIds',
										id: session.id,
									}),
								);
							}
						}}
					/>
				</div>
			</div>
		);
	};
	return (
		<>
			{isLoading ? (
				<Spin
					className="flex items-center justify-center p-2"
					tip="Loading"
					size="large"
				/>
			) : (
				<>
					<div className="select-none posture-collapse bg-gray-100">
						<Button.Group
							style={{
								width: '100%',
								justifyContent: 'end',
								padding: '10px',
							}}>
							{options.map(item => {
								const isSelected = selectedButton === item.value;
								return (
									<Button
										key={item.label}
										icon={item.icon}
										style={{
											borderColor: 'inherit',
											borderRadius: '0px',
											backgroundColor: isSelected ? '#D0D5DD' : 'white',
											color: '#1D2939',
										}}
										onClick={() => {
											if (item.value != 'filter') {
												setSelectedButton(item.value);
												if (!completedStatus && item.value === 'completed') {
													setCompletedStatus(true);
													fetchSessionData(session, 1, true);
												} else if (
													completedStatus &&
													item.value === 'incompleted'
												) {
													setCompletedStatus(false);
													fetchSessionData(session, 1, false);
												}
											}
										}}>
										{item.label}
									</Button>
								);
							})}
						</Button.Group>
						{customRomSessionExercise?.data?.length! > 0 ? (
							<>
								{customRomSessionExercise?.data?.map(
									(session: ICustomRomSession) => {
										return (
											<div className="header-panel !bg-white !border !border-gray-200 !rounded-lg my-1.5 p-3 flex justify-between items-center"
												title={session?.romPatientResults.length < 1 ? t('Admin.data.addToReports.noData') : ''}
  												style={{ cursor: session?.romPatientResults.length >= 1 ? 'pointer' : 'not-allowed' }}
											>
												<span
													onClick={e => {
														if (session?.romPatientResults.length >= 1){
														e.stopPropagation();
														dispatch(setSelectedRom(session));
														navigate(
															`/${user.isPhysioterapist ? selectedUser.id : user.id}/${URL_PATH.ROM}/${session?.id}/${URL_PATH.SCAN_RESULT}`,
															{
																state: {
																	status: completedStatus,
																},
															},
														);
													}
													}}>
													{session?.title ||
														moment(session?.createdAt).local().format('LLLL')}
												</span>
												<span>{healthStatus(session)}</span>
											</div>
										);
									},
								)}
							</>
						) : (
							<div className="coach-rom-summary-container-row-div">
								<img
									style={{ width: 100, height: 100 }}
									className="coach-rom-summary-container-row-div-img"
									src="/images/NoExe.png"
									alt="No Exercises Image"
								/>
								<h1 className="coach-rom-summary-container-row-div-h1">
									{t('Patient.data.myProgress.omniRom.empty')}
								</h1>
							</div>
						)}
					</div>

					{customRomSessionExercise?.pagination?.totalCount! >= 5 && (
						<Pagination
							className="flex justify-center"
							current={customRomSessionExercise?.pagination?.currentPage}
							showSizeChanger={false}
							onChange={onPageChange}
							total={customRomSessionExercise?.pagination?.totalCount}
						/>
					)}
				</>
			)}
		</>
	);
};

export default CustomSummarySession;
